// src/utils/pushConfig.js
import { getMessaging, getToken, onMessage } from "firebase/messaging";
import { doc, updateDoc } from "firebase/firestore";
import { db, app } from "../firebase";

// Your public VAPID key (from Firebase Console → Cloud Messaging → Web configuration)
const VAPID_KEY = "BCoZaVTPMjaAXun1vMKetksnP4_iA0rLWh_QdBYH4aHioVynmAt9sBq3NkJyKdc_8ZlKqTK58FVgp2awV5g1eLo";

/**
 * Sends a push notification to a specific device token
 */
export const sendPushNotification = async (fcmToken, payload) => {
  if (!fcmToken) {
    console.warn("No FCM token provided for push");
    return;
  }

  try {
    const API_KEY = "AIzaSyCJY2NGVKi_fvyR5KKwM_0QN3hoMJ95CtE"; // Your Firebase Web API Key
    const response = await fetch("https://fcm.googleapis.com/fcm/send", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `key=${API_KEY}`,
      },
      body: JSON.stringify({
        to: fcmToken,
        notification: {
          title: payload.title,
          body: payload.body,
          icon: payload.icon || "/logo192.png",
          click_action: "https://esdras-app-frontend.vercel.app/stylist/dashboard",
          sound: "default",
        },
        priority: "high",
      }),
    });

    const data = await response.json();
    console.log("FCM send response:", data);

    if (!response.ok) {
      throw new Error(`FCM error: ${data.error?.message || response.statusText}`);
    }

    return data;
  } catch (error) {
    console.error("Failed to send push notification:", error);
  }
};

/**
 * Requests notification permission, gets token, and saves to Firestore
 * This version is SAFE and will NEVER crash the app
 */
export const requestNotificationPermission = async (userId) => {
  // Early exit if browser doesn't support push
  if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) {
    console.warn("Push notifications are not supported in this browser");
    return null;
  }

  try {
    // Give extra time for service worker to fully activate (Vercel/prod fix)
    await new Promise((resolve) => setTimeout(resolve, 3000));

    // Wait for active service worker
    const registration = await navigator.serviceWorker.ready;

    // Request permission
    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
      console.log("Notification permission denied by user");
      return null;
    }

    // Get FCM token (safely wrapped)
    const messaging = getMessaging(app);
    const token = await getToken(messaging, { vapidKey: VAPID_KEY }).catch((err) => {
      console.warn("getToken failed (non-critical):", err);
      return null;
    });

    if (!token) {
      console.warn("No push token generated");
      return null;
    }

    // Save to users collection
    await updateDoc(doc(db, "users", userId), {
      fcmToken: token,
      notificationsEnabled: true,
      lastTokenRefresh: new Date(),
    }).catch((e) => console.warn("Failed to save user token:", e));

    // Try to save to barbers collection (if user is a barber)
    try {
      await updateDoc(doc(db, "barbers", userId), {
        pushToken: token,
        pushEnabled: true,
      });
      console.log("Barber-specific push token saved");
    } catch (e) {
      console.log("User is not a barber; skipping barber token sync");
    }

    console.log("Push token successfully synced to Firestore");
    return token;
  } catch (error) {
    // Catch EVERYTHING - this prevents white screen crashes
    console.error("Push permission/setup failed (non-critical):", error);
    return null;
  }
};

/**
 * Listen for foreground messages (when app is open)
 */
export const listenForForegroundMessages = () => {
  try {
    const messaging = getMessaging(app);
    onMessage(messaging, (payload) => {
      console.log("Foreground message received:", payload);

      // Show browser notification
      if (Notification.permission === "granted") {
        new Notification(payload.notification?.title || "Esdras Update", {
          body: payload.notification?.body || "New message received",
          icon: "/logo192.png",
        });
      }
    });
  } catch (err) {
    console.warn("Foreground message listener setup failed:", err);
  }
};

// Legacy export (if used elsewhere)
export const initPushNotifications = requestNotificationPermission;
